import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Headphones, Shield, Lock, Eye, Clock, CheckCircle2,
  Loader2, AlertTriangle, ArrowRight, ShieldCheck,
} from "lucide-react";
import SEO, { breadcrumb } from "../components/SEO";
import { API } from "../apiBase";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const SEO_SCHEMA = [
  breadcrumb([
    { name: "Accueil", url: "/" },
    { name: "Support à distance", url: "/support-distant" },
  ]),
];

export default function SupportDistant() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    organization: "",
    phone: "",
    reason: "",
    consent_remote: false,
    website: "", // honeypot
  });
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    const SCRIPT_ID = "cf-turnstile-script";
    const renderWidget = () => {
      if (!window.turnstile || !turnstileRef.current) return;
      try { if (widgetIdRef.current) window.turnstile.remove(widgetIdRef.current); }
      catch (err) { console.debug("Turnstile remove", err); }
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "dark",
        callback: (token) => setTurnstileToken(token),
        "error-callback": () => setTurnstileToken(""),
        "expired-callback": () => setTurnstileToken(""),
      });
    };
    if (window.turnstile) { renderWidget(); return; }
    if (!document.getElementById(SCRIPT_ID)) {
      const s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      s.async = true;
      s.defer = true;
      s.onload = renderWidget;
      document.head.appendChild(s);
    } else {
      const i = setInterval(() => {
        if (window.turnstile) { clearInterval(i); renderWidget(); }
      }, 200);
    }
  }, []);

  const handle = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_remote) {
      setStatus({ state: "error", msg: "Le consentement explicite est obligatoire pour autoriser la prise en main à distance." });
      return;
    }
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setStatus({ state: "error", msg: "Merci de valider le contrôle anti-robot avant d'envoyer." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      const payload = { ...form, turnstile_token: turnstileToken };
      await axios.post(`${API}/support/remote-request`, payload);
      setStatus({
        state: "success",
        msg: "Votre demande a été envoyée. Nous vous appelons dans les meilleurs délais au numéro indiqué.",
      });
      setForm({ ...form, full_name: "", email: "", organization: "", phone: "", reason: "", consent_remote: false, website: "" });
      try {
        if (window.turnstile && widgetIdRef.current) {
          window.turnstile.reset(widgetIdRef.current);
          setTurnstileToken("");
        }
      } catch (err) { /* noop */ }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({
        state: "error",
        msg: typeof detail === "string" ? detail : "Une erreur est survenue, merci de réessayer.",
      });
    }
  };

  return (
    <div className="bg-[var(--mc-navy-950)]" data-testid="page-support-distant">
      <SEO
        title="Support à distance MetaConnect — Prise en main sécurisée"
        description="Autorisez en quelques clics une prise en main à distance par l'équipe MetaConnect : connexion sécurisée, consentement explicite, audit RGPD, session unique."
        canonical="/support-distant"
        schema={SEO_SCHEMA}
      />

      {/* Hero */}
      <section className="relative pt-32 pb-12 lg:pt-40 hex-grid">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#0369A1]">
            <span className="h-px w-7 bg-[var(--mc-blue-400)]" />
            Support technique
          </div>
          <h1 className="font-display text-[#0F2B5B] text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] mt-4">
            Autoriser une prise en main
            <span className="bg-gradient-to-r from-[#7FB3FF] via-[#4F8FFF] to-[#2D7BFF] bg-clip-text text-transparent">
              {" "}à distance.
            </span>
          </h1>
          <p className="mt-6 text-[16px] text-[#475569] leading-relaxed max-w-2xl">
            Vous êtes au téléphone avec notre équipe support et nous avons besoin de prendre la main sur votre poste pour résoudre rapidement votre problème ?
            Remplissez ce formulaire — nous vous rappelons immédiatement et vous envoyons un lien sécurisé à usage unique.
          </p>

          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            <Reassure icon={<Shield size={16} />} title="Consentement explicite" desc="Vous validez chaque action. Vous pouvez révoquer à tout moment." />
            <Reassure icon={<Lock size={16} />} title="Connexion chiffrée" desc="Session unique, expirant en 30 min. Données stockées en France." />
            <Reassure icon={<Eye size={16} />} title="Vous voyez tout" desc="L'écran reste visible. Aucune action invisible n'est possible." />
          </div>
        </div>
      </section>

      {/* Formulaire */}
      <section className="relative pb-16 lg:pb-24">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="mc-card p-6 lg:p-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(45,123,255,0.18)] border border-[var(--mc-blue-400)] text-[#0F2B5B]">
                <Headphones size={18} />
              </span>
              <div>
                <h2 className="font-display text-[#0F2B5B] text-[18px]">Demande de prise en main à distance</h2>
                <p className="text-[12px] text-[#475569] mt-0.5">Nous vous rappelons sous 5-15 minutes en heures ouvrées.</p>
              </div>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="support-remote-form">
              {/* Honeypot (invisible aux humains) */}
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={handle("website")}
                tabIndex={-1}
                autoComplete="off"
                className="absolute opacity-0 pointer-events-none h-0 w-0"
                aria-hidden="true"
              />

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nom complet *">
                  <input
                    type="text"
                    required
                    value={form.full_name}
                    onChange={handle("full_name")}
                    placeholder="Ex : Benoît Martin"
                    data-testid="support-input-name"
                    className="mc-input"
                  />
                </Field>
                <Field label="Email *">
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={handle("email")}
                    placeholder="email@exemple.fr"
                    data-testid="support-input-email"
                    className="mc-input"
                  />
                </Field>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Téléphone (rappel) *">
                  <input
                    type="tel"
                    required
                    value={form.phone}
                    onChange={handle("phone")}
                    placeholder="06 12 34 56 78"
                    data-testid="support-input-phone"
                    className="mc-input"
                  />
                </Field>
                <Field label="Organisation (optionnel)">
                  <input
                    type="text"
                    value={form.organization}
                    onChange={handle("organization")}
                    placeholder="Ex : Optique Martin"
                    data-testid="support-input-org"
                    className="mc-input"
                  />
                </Field>
              </div>

              <Field label="Motif de la prise en main *">
                <textarea
                  required
                  rows={3}
                  value={form.reason}
                  onChange={handle("reason")}
                  placeholder="Décrivez brièvement le problème (ex : impossible de me connecter à OhMyVue, erreur lors d'une commande...)"
                  data-testid="support-input-reason"
                  className="mc-input"
                  maxLength={1000}
                />
              </Field>

              {/* Consentement RGPD explicite */}
              <div className="rounded-lg border border-slate-200 bg-[rgba(127,179,255,0.04)] p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.consent_remote}
                    onChange={handle("consent_remote")}
                    required
                    data-testid="support-consent-checkbox"
                    className="mt-1 h-4 w-4 rounded border-[var(--mc-blue-400)] accent-[var(--mc-blue-500)]"
                  />
                  <span className="text-[13px] text-[#334155] leading-relaxed">
                    <strong>J'autorise expressément l'équipe MetaConnect</strong> à prendre la main à distance sur mon poste informatique
                    via un outil de bureau distant sécurisé (type RustDesk / MeshCentral, hébergé en France),
                    dans le seul but de résoudre un problème technique que je viens d'exposer.
                    <br /><br />
                    Je comprends que :
                    <span className="block mt-2 ml-1 text-[12.5px] text-[#475569]">
                      • La connexion est <strong>chiffrée de bout en bout</strong>, à usage unique, expirant après 30 minutes
                      <br />• <strong>Mon écran reste visible</strong> en permanence, aucune action ne peut être réalisée à mon insu
                      <br />• Mon adresse IP et l'horodatage sont conservés <strong>12 mois maximum</strong> à des fins de preuve (audit RGPD)
                      <br />• Je peux <strong>révoquer mon consentement</strong> à tout moment en fermant la session ou en contactant <a href="mailto:contact@metaconnect.fr" className="link-underline text-[#0F2B5B]">contact@metaconnect.fr</a>
                      <br />• Aucune donnée de mon poste n'est copiée vers MetaConnect sans mon accord explicite à chaque étape
                    </span>
                  </span>
                </label>
              </div>

              {/* Turnstile */}
              {TURNSTILE_SITE_KEY && (
                <div className="flex justify-center pt-2">
                  <div ref={turnstileRef} data-testid="support-turnstile" />
                </div>
              )}

              {/* Status messages */}
              {status.state === "success" && (
                <div className="rounded-lg border border-[var(--mc-mint)] bg-[rgba(74,222,128,0.05)] p-4 flex items-start gap-3" data-testid="support-success">
                  <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
                  <p className="text-[13px] text-[#334155] leading-relaxed">{status.msg}</p>
                </div>
              )}
              {status.state === "error" && (
                <div className="rounded-lg border border-[#FCA5A5] bg-[rgba(252,165,165,0.06)] p-4 flex items-start gap-3" data-testid="support-error">
                  <AlertTriangle size={18} className="text-[#DC2626] shrink-0 mt-0.5" />
                  <p className="text-[13px] text-[#334155] leading-relaxed">{status.msg}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={status.state === "loading"}
                data-testid="support-submit-btn"
                className="btn-primary px-6 py-3 rounded-full text-[14px] font-medium inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status.state === "loading" ? (
                  <><Loader2 size={16} className="animate-spin" /> Envoi en cours…</>
                ) : (
                  <>Envoyer ma demande <ArrowRight size={16} /></>
                )}
              </button>

              <p className="text-[11.5px] text-[#475569] leading-relaxed pt-2 border-t border-[rgba(127,179,255,0.1)] mt-4">
                ⏱️ Réactivité : nous vous rappelons en général en moins de 15 minutes en heures ouvrées (9h-18h, lundi-vendredi).
                <br />🇫🇷 Données stockées en France · Conforme RGPD · Audit log accessible sur demande.
              </p>
            </form>
          </div>

          {/* RGPD info */}
          <div className="mt-8 mc-card p-5 lg:p-6">
            <div className="flex items-center gap-3 mb-3">
              <ShieldCheck size={18} className="text-emerald-600" />
              <h3 className="font-display text-[#0F2B5B] text-[15px]">Vos droits RGPD</h3>
            </div>
            <p className="text-[13px] text-[#475569] leading-relaxed">
              Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, d'effacement et de portabilité sur les données
              collectées lors de cette demande (nom, email, téléphone, motif, IP, horodatage).
              Pour exercer vos droits, écrivez à <a href="mailto:contact@metaconnect.fr" className="link-underline text-[#0F2B5B]">contact@metaconnect.fr</a>.
              Consultez notre <Link to="/donnees" className="link-underline text-[#0F2B5B]">Charte de protection des données</Link> pour plus d'informations.
            </p>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2 text-[12px] text-[#475569]">
            <Clock size={12} />
            <span>Besoin d'une démo plutôt qu'un support ?</span>
            <Link to="/demo" className="link-underline text-[#0F2B5B]" data-testid="support-link-demo">Planifier une démo</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[12px] uppercase tracking-[0.16em] text-[#0369A1] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function Reassure({ icon, title, desc }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-[rgba(127,179,255,0.03)] p-4">
      <div className="flex items-center gap-2 text-[#0F2B5B] mb-1.5">
        {icon}
        <span className="font-display text-[#0F2B5B] text-[13px]">{title}</span>
      </div>
      <p className="text-[12px] text-[#475569] leading-relaxed">{desc}</p>
    </div>
  );
}
